import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const LanguageSwitcher = () => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Get current language display text
  const getCurrentLanguageDisplay = () => {
    switch (i18n.language) {
      case 'fr':
        return 'FR';
      case 'en':
        return 'EN';
      default:
        return 'FR';
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center space-x-1 ${isMobile ? 'px-2 py-1 text-xs' : 'px-3 py-1.5'} rounded-full border border-white/50 bg-white/10 hover:bg-white/20 transition-all duration-200 focus:outline-none shadow-sm`}
        aria-expanded={isOpen}
      >
        {!isMobile && (
          <svg 
            className="w-4 h-4 text-white" 
            fill="currentColor" 
            viewBox="0 0 20 20"
          >
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM4.332 8.027a6.012 6.012 0 011.912-2.706C6.512 5.73 6.974 6 7.5 6A1.5 1.5 0 019 7.5V8a2 2 0 004 0 2 2 0 011.523-1.943A5.977 5.977 0 0116 10c0 .34-.028.675-.083 1H15a2 2 0 00-2 2v2.197A5.973 5.973 0 0110 16v-2a2 2 0 00-2-2 2 2 0 01-2-2 2 2 0 00-1.668-1.973z" clipRule="evenodd" />
          </svg>
        )}
        <span className={`font-medium ${isMobile ? 'text-xs' : 'text-sm'} text-white`}>{getCurrentLanguageDisplay()}</span>
        <svg 
          className={`${isMobile ? 'w-3 h-3' : 'w-3.5 h-3.5'} text-white transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div 
          className={`absolute ${isMobile ? 'right-0 w-36' : 'right-0 w-36'} mt-2 py-2 bg-white rounded-lg shadow-xl z-[9999] text-primary animate-fadeIn`}
        >
          <button
            className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center space-x-2 ${i18n.language === 'fr' ? 'font-semibold bg-gray-50 text-primary' : ''}`}
            onClick={() => changeLanguage('fr')}
          >
            <span className="w-5 h-5 flex items-center justify-center text-base">🇫🇷</span>
            <span>Français</span>
          </button>
          <button
            className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center space-x-2 ${i18n.language === 'en' ? 'font-semibold bg-gray-50 text-primary' : ''}`}
            onClick={() => changeLanguage('en')}
          >
            <span className="w-5 h-5 flex items-center justify-center text-base">🇬🇧</span>
            <span>English</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default LanguageSwitcher; 